import { Check, ChevronsUpDown } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/**
 * OKS-188 — Brand-profile timezone picker.
 *
 * Replaces the old 9-entry hardcoded `<Select>` with a searchable
 * Combobox (Radix `Popover` + cmdk `Command`) over the FULL IANA set
 * (`Intl.supportedValuesOf("timeZone")`, ~418 zones), keeping the
 * curated `TIMEZONES_COMMON` group pinned on top. A US/APAC/LATAM
 * brand admin can now self-serve their zone instead of asking CTPO to
 * set it from the Rails console.
 *
 * Two in-repo patterns are combined here:
 *   - Popover + cmdk Combobox structure — `MasterclassCompletionField`.
 *   - Hidden-input → Inertia `<Form>` bridge — `OnboardingCountrySelect`.
 *
 * Why the hidden-input bridge: shadcn/Radix controls render an
 * accessible button-trigger + portal, NOT a native form control with a
 * `name`. The selected value lives in component state. Inertia `<Form>`
 * only serialises native controls (`<input>`/`<select>`/`<textarea>`)
 * with `name`, so the hidden `<input>` is what actually reaches Rails.
 * `required` propagates to it so native constraint validation still
 * fires — but `Brand#timezone=` (server-side `ActiveSupport::TimeZone[]`
 * validation) is the real gate.
 *
 * **Wire contract** (BE strong-params + Vitest canary): unchanged from
 * the old Select — `name="brand[timezone]"`, value = raw IANA id.
 *
 * **A11y:** trigger carries `id` + `data-testid` (Label `htmlFor`
 * association intact), `role="combobox"`, `aria-expanded`; `aria-invalid`
 * flips on error and `aria-describedby` links the `<InputError>` block.
 * Keyboard + typeahead come from cmdk + Radix Popover out of the box.
 */

/**
 * Curated zones pinned to the top "Common" group, kept verbatim from the
 * pre-OKS-188 `TIMEZONES_PRIORITY` constant (no re-curation in a
 * tech-debt ticket — PRD Open Q4). Order is intentional and asserted by
 * the Vitest canary.
 */
const TIMEZONES_COMMON = [
  "Europe/Paris",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Tokyo",
  "UTC",
] as const

/**
 * `Intl.supportedValuesOf` is ES2022; this project's tsconfig targets
 * ES2020 (`tsconfig.app.json`), so the symbol is absent from the TS lib
 * types — a bare call fails `npm run check` (tsc). We read it through a
 * narrow typed cast (NOT `as any`, which `eslint --max-warnings 0`
 * rejects). The optional chain + `?? []` doubles as the graceful
 * degradation path: browsers without the API (PRD AC8) fall back to the
 * COMMON-only list and the picker is never empty.
 */
function supportedTimeZones(): string[] {
  return (
    (
      Intl as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("timeZone") ?? []
  )
}

export interface BrandTimezoneSelectProps {
  /** Rails strong-params key — `brand[timezone]`. Drives the hidden input. */
  name: string
  /** DOM id on the trigger — paired with the parent `<Label htmlFor>`. */
  id: string
  /** Stable test selector base; suffixes derive `-hidden` / `-content` / `-option-{tz}`. */
  testId: string
  /** Pre-fill from `brand.timezone` (always resolves — model default `Europe/Paris`). */
  defaultValue?: string
  /** Inertia render-prop error array for this field; flips `aria-invalid`. */
  errorMessages?: string[]
  /** DOM id of the corresponding `<InputError>`; wired into `aria-describedby`. */
  errorId?: string
  /** Read-only tier (`can_edit: false`) → disabled trigger. */
  disabled?: boolean
  /** Native-level `required` on the hidden input. */
  required?: boolean
}

export function BrandTimezoneSelect({
  name,
  id,
  testId,
  defaultValue = "",
  errorMessages,
  errorId,
  disabled = false,
  required = false,
}: BrandTimezoneSelectProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState<string>(defaultValue)
  const [open, setOpen] = useState(false)

  // Build the two groups synchronously (no fetch → no loading state).
  // Recomputes only when the selection changes (relevant solely for the
  // legacy-zone prepend below).
  const { common, rest } = useMemo(() => {
    const all = supportedTimeZones()
    const allSet = new Set(all)

    // Stored-zone-always-selectable guarantee (PRD AC5): a legacy /
    // console-set zone that is absent from BOTH the Intl list AND the
    // curated set is prepended to the Common group so it always shows +
    // checks. NOT a separate labeled group — that would need a 5th i18n
    // heading key and trip the OKS-323 sibling-key parity lint.
    const commonList =
      value && !allSet.has(value) && !TIMEZONES_COMMON.includes(value as never)
        ? [value, ...TIMEZONES_COMMON]
        : [...TIMEZONES_COMMON]

    const commonSet = new Set(commonList)
    const restList = all.filter((tz) => !commonSet.has(tz)).sort()

    return { common: commonList, rest: restList }
  }, [value])

  const isInvalid = errorMessages !== undefined && errorMessages.length > 0

  function handleSelect(tz: string) {
    setValue(tz)
    setOpen(false)
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              role="combobox"
              id={id}
              aria-expanded={open}
              aria-invalid={isInvalid ? "true" : undefined}
              aria-describedby={isInvalid && errorId ? errorId : undefined}
              disabled={disabled}
              data-testid={testId}
              className="w-full justify-between font-normal"
            >
              {/* Trigger always shows the current zone — `brand.timezone`
                resolves to the model default, so there is no blank state. */}
              <span className="truncate">{value}</span>
              <ChevronsUpDown
                className="ml-2 size-4 shrink-0 opacity-50"
                aria-hidden="true"
              />
            </Button>
          }
        />
        <PopoverContent
          align="start"
          // Pin width to the trigger so the list never overflows at 375px
          // (Tailwind v4 CSS-var arbitrary value). `p-0` lets the Command
          // own its own padding.
          className="w-(--radix-popover-trigger-width) p-0"
          data-testid={`${testId}-content`}
        >
          <Command>
            <CommandInput
              placeholder={t(
                "settings.brand.profile.timezone_search_placeholder",
              )}
            />
            <CommandList>
              <CommandEmpty>
                {t("settings.brand.profile.timezone_empty")}
              </CommandEmpty>
              <CommandGroup
                heading={t("settings.brand.profile.timezone_group_common")}
              >
                {common.map((tz) => (
                  <TimezoneItem
                    key={tz}
                    tz={tz}
                    testId={testId}
                    selected={tz === value}
                    onSelect={handleSelect}
                  />
                ))}
              </CommandGroup>
              {rest.length > 0 && (
                <CommandGroup
                  heading={t("settings.brand.profile.timezone_group_all")}
                >
                  {rest.map((tz) => (
                    <TimezoneItem
                      key={tz}
                      tz={tz}
                      testId={testId}
                      selected={tz === value}
                      onSelect={handleSelect}
                    />
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/*
        Hidden input bridges the Radix-controlled state into the native
        form payload. `name` is the Rails strong-params key; `value` is
        the raw IANA id (the wire-format canary verifies this).
      */}
      <input
        type="hidden"
        name={name}
        value={value}
        required={required}
        data-testid={`${testId}-hidden`}
      />
    </>
  )
}

/**
 * One zone row. cmdk filters on the `value` prop (substring,
 * case-insensitive), so typing `singapore` reaches `Asia/Singapore`. We
 * read `tz` from closure in `onSelect` rather than the cmdk-provided arg
 * to avoid cmdk's historical value-lowercasing.
 */
function TimezoneItem({
  tz,
  testId,
  selected,
  onSelect,
}: {
  tz: string
  testId: string
  selected: boolean
  onSelect: (tz: string) => void
}) {
  return (
    <CommandItem
      value={tz}
      onSelect={() => onSelect(tz)}
      className="cursor-pointer"
      data-testid={`${testId}-option-${tz}`}
    >
      <Check
        className={cn("mr-2 size-4", selected ? "opacity-100" : "opacity-0")}
        aria-hidden="true"
      />
      <span className="truncate">{tz}</span>
    </CommandItem>
  )
}

export default BrandTimezoneSelect
